import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import './index.css'
import App from './App.jsx'

function setupAppViewportVars() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (window.__ondaxViewportVarsSetup) return
  window.__ondaxViewportVarsSetup = true

  const params = (() => {
    try {
      return new URLSearchParams(window.location.search || '')
    } catch {
      return null
    }
  })()
  const debugViewport = Boolean(params && (params.get('debugViewport') === '1' || params.get('debugViewport') === 'true'))
  if (debugViewport) {
    document.documentElement.dataset.debugViewport = '1'
  }

  let debugEl = null
  const ensureDebug = () => {
    if (!debugViewport || debugEl) return
    debugEl = document.createElement('div')
    debugEl.id = 'viewport-debug'
    debugEl.style.position = 'fixed'
    debugEl.style.left = '8px'
    debugEl.style.right = '8px'
    debugEl.style.top = '8px'
    debugEl.style.zIndex = '99999'
    debugEl.style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
    debugEl.style.fontSize = '12px'
    debugEl.style.lineHeight = '1.2'
    debugEl.style.color = '#111'
    debugEl.style.background = 'rgba(255,255,255,0.92)'
    debugEl.style.border = '2px solid #ff3b30'
    debugEl.style.borderRadius = '10px'
    debugEl.style.padding = '8px 10px'
    debugEl.style.pointerEvents = 'none'
    document.body.appendChild(debugEl)
  }

  const isIOS = (() => {
    try {
      const ua = String(window.navigator?.userAgent || '')
      const platform = String(window.navigator?.platform || '')
      const isAppleTouch = platform === 'MacIntel' && typeof window.navigator?.maxTouchPoints === 'number' && window.navigator.maxTouchPoints > 1
      return /iPad|iPhone|iPod/i.test(ua) || /iPad|iPhone|iPod/i.test(platform) || isAppleTouch
    } catch {
      return false
    }
  })()

  let rafId = null
  let focusRafId = null

  const isTextInput = (el) => {
    if (!el) return false
    const tag = String(el.tagName || '').toLowerCase()
    if (tag === 'textarea') return true
    if (tag !== 'input') return false
    const type = String(el.getAttribute?.('type') || '').toLowerCase()
    // Tipos que costumam abrir teclado e quebrar viewport no iOS.
    if (!type) return true
    return !['checkbox', 'radio', 'range', 'button', 'submit', 'reset', 'file', 'color'].includes(type)
  }

  const scrollActiveIntoView = () => {
    focusRafId = null
    const active = document.activeElement
    if (!isTextInput(active) && !active?.isContentEditable) return
    // SAC mobile controla o layout do composer localmente; forçar scrollIntoView
    // aqui pode deslocar a viewport e criar faixa/branco durante digitação.
    if (active?.closest?.('.sac-page')) return
    try {
      // iOS Safari pode ficar com scroll fora do range após resize do teclado.
      active.scrollIntoView({ block: 'center', inline: 'nearest' })
    } catch {
      // ignore
    }
  }

  const scheduleFocusFix = () => {
    if (focusRafId !== null) return
    focusRafId = window.requestAnimationFrame(scrollActiveIntoView)
  }

  const apply = () => {
    rafId = null
    const docClientHeight = document.documentElement?.clientHeight || 0
    const innerHeight = window.innerHeight || 0
    const vv = window.visualViewport || null
    const vvHeight = vv?.height || 0
    const vvOffsetTop = vv?.offsetTop || 0

    // Altura do app: usa innerHeight (mais estável em iOS/webviews) e cai para clientHeight.
    const appHeight = (innerHeight > 0 ? innerHeight : docClientHeight)
    if (!appHeight) return
    document.documentElement.style.setProperty('--app-dvh', `${Math.round(appHeight)}px`)
    document.documentElement.style.setProperty('--vh', `${(appHeight * 0.01).toFixed(4)}px`)

    // Inset do teclado/overlay: quanto da área inferior ficou “coberta” (teclado + barras do webview).
    // Em Safari normal, visualViewport.height tende a refletir a área visível acima do teclado.
    // Em webviews (WA Business), isso costuma ser a forma mais confiável de levantar o composer.
    const visibleHeight = (vvHeight > 0 ? vvHeight : (docClientHeight > 0 ? docClientHeight : appHeight))
    const kbInsetRaw = Math.max(0, (appHeight - (visibleHeight + vvOffsetTop)))
    const kbInset = Number.isFinite(kbInsetRaw) ? kbInsetRaw : 0
    document.documentElement.style.setProperty('--kb-inset', `${Math.round(kbInset)}px`)

    // Sinaliza quando o teclado está aberto (principalmente iOS/Safari) para ajustes de safe-area no CSS.
    try {
      const active = document.activeElement
      const focusLikelyKeyboard = Boolean(isTextInput(active) || active?.isContentEditable)
      const keyboardOpen = Boolean((kbInset > 30) || focusLikelyKeyboard)
      document.documentElement.dataset.kb = keyboardOpen ? '1' : '0'
    } catch {
      // ignore
    }

    if (debugViewport) {
      ensureDebug()
      const delta = Math.max(0, Math.round(kbInset))
      const offsetTop = vvOffsetTop
      const scrollY = typeof window.scrollY === 'number' ? window.scrollY : 0
      const docH = document.documentElement?.clientHeight || 0
      const appDvh = getComputedStyle(document.documentElement).getPropertyValue('--app-dvh').trim()
      const kb = document.documentElement.dataset.kb
      const ae = document.activeElement
      const tag = ae ? String(ae.tagName || '').toLowerCase() : '-'
      const rectBottom = ae && ae.getBoundingClientRect ? Math.round(ae.getBoundingClientRect().bottom) : null
      const rectTop = ae && ae.getBoundingClientRect ? Math.round(ae.getBoundingClientRect().top) : null
      const vvH = vvHeight ? Math.round(vvHeight) : null
      const inH = Math.round(appHeight || 0)
      const effH = Math.round((visibleHeight + vvOffsetTop) || 0)

        const measure = (selector) => {
          const el = document.querySelector(selector)
          if (!el || !el.getBoundingClientRect) return null
          const r = el.getBoundingClientRect()
          const bottom = Math.round(r.bottom)
          const top = Math.round(r.top)
          const gap = effH ? Math.round(effH - bottom) : null
          return { selector, top, bottom, gap }
        }

        const mAppContent = measure('.app-content')
        const mSacComposer = measure('.sac-composer')
        const mSacReply = measure('.sac-reply-bar')
        const mChatReply = measure('.chat-reply-bar')

      debugEl.textContent = [
        `debugViewport=1 kb=${kb}`,
        `innerHeight=${inH} visualViewport.height=${vvH ?? '-'} doc.clientHeight=${Math.round(docH)} offsetTop=${Math.round(offsetTop)}`,
        `visibleBottom=${effH} kbInset=${Math.round(kbInset)}px` ,
        `delta(kbInset)=${Math.round(delta)} scrollY=${Math.round(scrollY)}`,
        `--app-dvh=${appDvh}`,
        `active=${tag} rectTop=${rectTop ?? '-'} rectBottom=${rectBottom ?? '-'}`,
          mAppContent ? `app-content bottom=${mAppContent.bottom} gap=${mAppContent.gap}` : 'app-content: -',
          mSacComposer ? `sac-composer bottom=${mSacComposer.bottom} gap=${mSacComposer.gap}` : 'sac-composer: -',
          mSacReply ? `sac-reply-bar bottom=${mSacReply.bottom} gap=${mSacReply.gap}` : 'sac-reply-bar: -',
          mChatReply ? `chat-reply-bar bottom=${mChatReply.bottom} gap=${mChatReply.gap}` : 'chat-reply-bar: -',
      ].join('\n')
    }

    // iOS Safari: depois do resize do teclado, o scroll pode ficar fora do range e a tela fica “em branco”.
    if (isIOS && window.visualViewport) {
      try {
        const doc = document.documentElement
        const maxScroll = Math.max(0, (doc?.scrollHeight || 0) - appHeight)
        if (typeof window.scrollY === 'number' && window.scrollY > maxScroll + 1) {
          window.scrollTo(0, maxScroll)
        }
      } catch {
        // ignore
      }
    }

    // Importante: não forçar scrollIntoView em todo recalculo de viewport.
    // Em alguns webviews isso entra em loop (resize/scroll) e causa tremor.
    // O ajuste de foco continua acontecendo no evento focusin.
  }
  const schedule = () => {
    if (rafId !== null) return
    rafId = window.requestAnimationFrame(apply)
  }

  apply()

  window.addEventListener('resize', schedule, { passive: true })
  window.addEventListener('orientationchange', schedule, { passive: true })
  window.addEventListener('focusin', () => {
    schedule()
    scheduleFocusFix()
  }, { passive: true })
  window.addEventListener('focusout', schedule, { passive: true })

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', schedule, { passive: true })
    window.visualViewport.addEventListener('scroll', schedule, { passive: true })
  }
}

setupAppViewportVars()

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>,
)
